import * as Yup from 'yup';
import { t } from '@lingui/macro';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { ObjectShape } from 'yup/lib/object';
import StringSchema from 'yup/lib/string';
import Lazy from 'yup/lib/Lazy';

import { DATE_FORMAT } from 'components/constants';

export const REQUIRED_MESSAGE = t`Required`;

export const PASSWORD_SCHEMA = Yup.string()
  .trim()
  .min(10, t`Password has to be longer than 10 characters!`)
  .matches(/^(?=.*[A-Z]).{1,}$/, t`Password must contain at least 1 uppercase letter.`)
  .matches(/^(?=.*[!@*#$&%^{}[\]]).{1,}$/, t`Password must have at least 1 special character.`)
  .required('Required');

export const EMAIL_SCHEMA = Yup.string()
  .trim()
  .transform(value => (value && value.trim ? value.trim() : value))
  .email(t`Must be a valid email address.`);

export const NAME_SCHEMA = Yup.string()
  .trim()
  .min(1, REQUIRED_MESSAGE)
  .transform(value => (value && value.trim ? value.trim() : value))
  .matches(/[^,@+]/g, t`Name cannot contain the following: ,@+.`);

export const MAX_DATE_SCHEMA = Yup.date()
  .nullable()
  .max(moment().add(1, 'day').toDate(), t`Date cannot be in the future.`);

export const LINKED_IN_SCHEMA = Yup.string().matches(
  /^(https?:\/\/)?([a-z]{2,3}\.)?linkedin\.com\/(in\/)?.*$/i,
  {
    message: 'Must be a valid LinkedIn url.',
    excludeEmptyString: true,
  }
);

export const PHONE_NUMBER_SCHEMA = Yup.string().matches(
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
  { message: 'Must be a valid phone number.', excludeEmptyString: true }
);

export const skillMapsWithLevels = Yup.array().of(
  Yup.object().shape({
    skillMap: Yup.object().shape({
      skillMapId: Yup.number(),
    }),
    targetSkillMapLevelIds: Yup.array().of(Yup.number()),
  })
);

export const validateStringInput = (value: unknown) => {
  let error;
  if (typeof value === 'string' && !value.trim()) {
    error = REQUIRED_MESSAGE;
  }
  return error;
};

export const EXTERNAL_JOB_URL_REGEX =
  // eslint-disable-next-line no-useless-escape
  /(?:[a-z0-9]+)\.greenhouse\.io\/sdash\/(\d+)|(?:[a-z0-9]+)\.greenhouse\.io\/plans\/(\d+)(?:\/setup)?|hire(?:\.sandbox)?\.lever\.co\/jobs\/postings\/edit\/([a-z0-9\-]+)/i;

export const EXTERNAL_JOB_URLS = Yup.array<Lazy<StringSchema>>().of(
  Yup.lazy((value: string) =>
    value
      ? Yup.string().matches(EXTERNAL_JOB_URL_REGEX, 'Please copy your job url again.')
      : Yup.string().nullable()
  ) as unknown as Yup.BaseSchema
);

export async function validationCallback<Values>(
  form: Values,
  validationSchema: Yup.ObjectSchema<ObjectShape>,
  startDateKey = 'startDate' as keyof Values,
  endDateKey = 'endDate' as keyof Values
) {
  const errors = {} as Record<string, string[]>;

  try {
    await validationSchema.validate(form, {
      abortEarly: false,
    });
  } catch (validationError) {
    const { inner } = validationError as Yup.ValidationError;

    inner.reduce((acc, err) => {
      acc[err.path as string] = acc[err.path as string] || [];
      acc[err.path as string].push(err.message);
      return acc;
    }, errors);
  }
  const overlapStartDate = moment(form[startDateKey] as moment.MomentInput, DATE_FORMAT);
  const overlapEndDate = moment(form[endDateKey] as moment.MomentInput, DATE_FORMAT);

  if (overlapEndDate.isBefore(overlapStartDate)) {
    errors[endDateKey as string] = ['End date must be after start date'];
  }
  if (!isEmpty(errors)) {
    return errors;
  }
}
